'use client';

import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { CircledClose } from '@/shared-ui/icons/circled-close';
import { type ComponentPropsWithoutRef, type ReactElement, type Ref } from 'react';

type DrawerVariant = 'left' | 'right' | 'bottom';

type DrawerRootProps = ComponentPropsWithoutRef<typeof Sheet>;

type DrawerCloseButtonProps = ComponentPropsWithoutRef<'button'> & {
    variant?: DrawerVariant;
};

const Root = ({ children, ...props }: DrawerRootProps): ReactElement => {
    return <Sheet {...props}>{children}</Sheet>;
};

Root.displayName = 'Drawer.Root';

type DrawerContentProps = Omit<ComponentPropsWithoutRef<typeof SheetContent>, 'side' | 'overlayClassName'> & {
    variant?: DrawerVariant;
    title?: string;
    overlayClassName?: string;
    ref?: Ref<HTMLDivElement>;
};

const Content = ({
    className,
    children,
    ref,
    variant = 'left',
    title = 'Drawer content',
    overlayClassName,
    ...props
}: DrawerContentProps): ReactElement => {
    return (
        <SheetContent
            ref={ref}
            data-drawer-variant={variant}
            side={variant}
            overlayClassName={cn('bg-primary/60', overlayClassName)}
            className={cn(
                'group/drawer-content overflow-visible p-0 [&>button:last-child]:hidden data-[state=open]:duration-300 data-[state=open]:ease-in-out data-[state=closed]:duration-300 data-[state=closed]:ease-in-out',
                variant === 'bottom' && 'max-h-[calc(100dvh-48px)]',
                className
            )}
            {...props}>
            <SheetTitle className="sr-only">{title}</SheetTitle>
            {children}
        </SheetContent>
    );
};

Content.displayName = 'Drawer.Content';

const CloseButton = ({ className, variant, ...props }: DrawerCloseButtonProps): ReactElement => {
    return (
        <SheetClose asChild>
            <button
                type="button"
                data-drawer-variant={variant}
                className={cn(
                    'absolute size-8 cursor-pointer',
                    // Left variant
                    'group-data-[drawer-variant=left]/drawer-content:right-[-40px] group-data-[drawer-variant=left]/drawer-content:top-2', // icon is 32px wide, so we offset it by 40px to give it an 8px margin from the edge of the drawer
                    // Right variant
                    'group-data-[drawer-variant=right]/drawer-content:left-[-40px] group-data-[drawer-variant=right]/drawer-content:top-2', // icon is 32px wide, so we offset it by 40px to give it an 8px margin from the edge of the drawer
                    // Bottom variant
                    'group-data-[drawer-variant=bottom]/drawer-content:right-2 group-data-[drawer-variant=bottom]/drawer-content:top-[-40px]',
                    className
                )}
                aria-label="Close"
                {...props}>
                <span aria-hidden="true" className="flex items-center justify-center">
                    <Icon icon={CircledClose} />
                </span>
            </button>
        </SheetClose>
    );
};

CloseButton.displayName = 'Drawer.CloseButton';

const Drawer = {
    Root,
    Content,
    CloseButton,
} as const;

export { Drawer };
